import type { Deckung, TariffVariation, TermType } from './deckung';

/**
 * The read-only views over a `tarif_deckung` document: the "Tarifübersicht" comparison (ui-spec §4)
 * and the tarif generation preview (§10). Everything here is DERIVED from the pinned document —
 * nothing is authored, and nothing is stored twice.
 */

// ---------------------------------------------------------------------------------------------
// The comparison table (ui-spec §4).
// ---------------------------------------------------------------------------------------------

/** One cell of the comparison: what a variant covers for one category. */
export type OverviewCell =
  | { readonly kind: 'none' }
  | { readonly kind: 'percent'; readonly amount: number; readonly limit?: number }
  | { readonly kind: 'fixed'; readonly amount: number };

/** The table has something to compare only with at least one variant AND one category (v1). */
export const hasOverviewData = (doc: Deckung): boolean =>
  doc.tariffVariations.length > 0 && doc.categoryDefinitions.length > 0;

/**
 * The cell for `category` under `variant`: "–" when the category is not assigned or not covered;
 * otherwise the term's value. A `{isCovered: true}` without a type of its own is covered by the
 * variant's TARIFF term, so that is what the cell shows. A percent `limit` is kept only when it
 * is above 0 — v1 writes ", bis zu …" only then.
 */
export function overviewCell(variant: TariffVariation, category: string): OverviewCell {
  const assigned = variant.categories.find((c) => c.key === category);
  if (assigned?.coverageTerm.isCovered !== true) return { kind: 'none' };
  const own = assigned.coverageTerm;
  const type: TermType | undefined = own.type ?? variant.coverageTerm.type;
  const value = own.type !== undefined ? own.value : variant.coverageTerm.value;
  if (type === undefined || value === undefined) return { kind: 'none' };
  if (type === 'percent') {
    return value.limit !== undefined && value.limit > 0
      ? { kind: 'percent', amount: value.amount, limit: value.limit }
      : { kind: 'percent', amount: value.amount };
  }
  return { kind: 'fixed', amount: value.amount };
}

/** The labels of the given category keys, in catalogue order; unknown keys by their key. */
export function categoryLabelsOf(doc: Deckung, keys: readonly string[]): string[] {
  const wanted = new Set(keys);
  const known = doc.categoryDefinitions.filter((c) => wanted.has(c.key));
  const unknown = keys.filter((key) => !doc.categoryDefinitions.some((c) => c.key === key));
  return [...known.map((c) => c.label), ...unknown];
}

// ---------------------------------------------------------------------------------------------
// Group mode (ui-spec §5): which parts of the resolved document came from the group tarif.
// ---------------------------------------------------------------------------------------------

export type Sources = Readonly<Record<string, string>>;

export const isGroupSourced = (sources: Sources | undefined, key: string): boolean =>
  sources?.[key] === 'GROUP_TARIF';

/** A category cell overridden by the group: `tariffVariations.<V>.categories.<C>`. */
export const categoryFromGroup = (
  sources: Sources | undefined,
  variant: string,
  category: string,
): boolean => isGroupSourced(sources, `tariffVariations.${variant}.categories.${category}`);

/** A variant's own term or deductibles overridden — what its column header states. */
export const variantHeaderFromGroup = (sources: Sources | undefined, variant: string): boolean =>
  isGroupSourced(sources, `tariffVariations.${variant}.coverageTerm`) ||
  isGroupSourced(sources, `tariffVariations.${variant}.deductibles`);

/** Any section of a variant overridden — the preview card's "Gruppentarif" marker. */
export const variantFromGroup = (sources: Sources | undefined, variant: string): boolean =>
  Object.entries(sources ?? {}).some(
    ([key, source]) => source === 'GROUP_TARIF' && key.startsWith(`tariffVariations.${variant}.`),
  );

// ---------------------------------------------------------------------------------------------
// The generation preview (ui-spec §10).
// ---------------------------------------------------------------------------------------------

/** The SDD-016 version lifecycle as the version list reads it (`tarifs/model/version-list`). */
export type Lifecycle = 'DRAFT' | 'PUBLISHED' | 'ACTIVE' | 'RETIRED' | 'DISCARDED';

/** The preview's chip vocabulary: a RETIRED version reads "Archiviert". */
export type PreviewStatus = 'ACTIVE' | 'ARCHIVED' | 'DRAFT' | 'PUBLISHED';

export const previewStatusOf = (lifecycle: Exclude<Lifecycle, 'DISCARDED'>): PreviewStatus =>
  lifecycle === 'RETIRED' ? 'ARCHIVED' : lifecycle;

/** The StatusChip token a preview status is coloured with. */
export const statusTokenOf = (status: PreviewStatus): string =>
  status === 'ARCHIVED' ? 'RETIRED' : status;

/**
 * A generation's status (ui-spec §10): Aktiv with an ACTIVE version; Archiviert when every version
 * is RETIRED; Entwurf with drafts only; otherwise Veröffentlicht. Discarded drafts do not count;
 * `undefined` for a generation without a version.
 */
export function generationStatusOf(lifecycles: readonly Lifecycle[]): PreviewStatus | undefined {
  const counted = lifecycles.filter((l) => l !== 'DISCARDED');
  if (counted.length === 0) return undefined;
  if (counted.includes('ACTIVE')) return 'ACTIVE';
  if (counted.every((l) => l === 'RETIRED')) return 'ARCHIVED';
  if (counted.every((l) => l === 'DRAFT')) return 'DRAFT';
  return 'PUBLISHED';
}

/** `major.minor`, or undefined for a draft id. */
const numberOf = (name: string): readonly [number, number] | undefined => {
  const match = /^(\d+)\.(\d+)$/.exec(name);
  return match === null ? undefined : [Number(match[1]), Number(match[2])];
};

/**
 * Versions newest first: open drafts on top (they will become the next number), then by number,
 * descending and NUMERIC — `1.10` is newer than `1.9`.
 */
export function newestVersionsFirst<T>(
  versions: readonly T[],
  nameOf: (version: T) => string,
): T[] {
  return [...versions].sort((a, b) => {
    const x = numberOf(nameOf(a));
    const y = numberOf(nameOf(b));
    if (x === undefined || y === undefined) return x === undefined ? (y === undefined ? 0 : -1) : 1;
    return x[0] !== y[0] ? y[0] - x[0] : y[1] - x[1];
  });
}

/** Generations newest first: by `validFrom` descending, then by name descending. */
export function newestGenerationsFirst<
  T extends { readonly name?: string | undefined; readonly validFrom?: string | undefined },
>(generations: readonly T[]): T[] {
  return [...generations].sort((a, b) => {
    const byDate = (b.validFrom ?? '').localeCompare(a.validFrom ?? '');
    return byDate !== 0 ? byDate : (b.name ?? '').localeCompare(a.name ?? '');
  });
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** A manifest's `refs.<key>` pin, or '' — the manifest is a Struct, read defensively. */
export function manifestRef(manifest: unknown, key: string): string {
  const refs = isRecord(manifest) && isRecord(manifest.refs) ? manifest.refs : {};
  const value = refs[key];
  return typeof value === 'string' ? value : '';
}

/** The manifest with `refs.tarifDeckung` set — every other key carried through. */
export function withDeckungPin(manifest: unknown, ern: string): Record<string, unknown> {
  const base = isRecord(manifest) ? manifest : {};
  const refs = isRecord(base.refs) ? base.refs : {};
  return { ...base, refs: { ...refs, tarifDeckung: ern } };
}

/** `tarifservice:fahrrad/tarif_deckung.v1` → `{slug: 'fahrrad', name: 'tarif_deckung.v1'}`; undefined otherwise. */
export function assetOfErn(
  ern: string,
): { readonly slug: string; readonly name: string } | undefined {
  const match = /^tarifservice:([^/]+)\/([^/]+)$/.exec(ern);
  if (match === null) return undefined;
  return { slug: match[1] ?? '', name: match[2] ?? '' };
}
